import { defineStore } from 'pinia';
import { useRoute } from 'vue-router';
import { useAppStore } from './app';
import { useUserStore } from './user';
import Promotion from '../types/interfaces/promotion/Promotion';
import SuggestedPromotion from '../types/interfaces/promotion/SuggestedPromotion';
import EnabledPromotion from '../types/interfaces/promotion/EnabledPromotion';
import PromotionPackage from '../types/interfaces/promotion/PromotionPackage';
import serverFetch from '../globals/ServerFetch';

// eslint-disable-next-line import/prefer-default-export
export const usePromotionStore = defineStore({
	id: 'promotion',
	state: () => ({
		customerId: useAppStore().isAdminModule ? Number(useRoute()?.params?.id) : useUserStore().customerId,
		cartPromotions: [] as Promotion[],
		selectedPromotionId: 0,
		isPromotionEnabled: false,
		enabledPromotions: [] as EnabledPromotion[],
		isLimitModalVisible: false,
		isFeedbackModalVisible: false,
		isDisableRenewModalVisible: false,
		isSummaryPage: false,
		mobileScrollTopPosition: 0,
		promotionPackages: [] as PromotionPackage[],
		isServerError: false,
		isUnpaidInvoice: false,
		isNotEnoughPoints: false,
		isPromotionModalVisible: false,
		accountPoints: 0,
		enabledListsLength: 0,
		categoryInput: '',
		locationInput: '',
		selectedCategoryId: '',
		selectedLocationId: '',
		suggestions: [] as SuggestedPromotion[],
		suggestionsCheckboxFilter: true,
		apiNamespace: useAppStore().isAdminModule ? 'admin' : 'promotion',
		isFillProfileModalVisible: false,
		isSummaryPage: false,
		promotionIndicator: {
			hasGallery: false,
			hasMarks: false,
			hasDescription: false,
		},
		showConfirmationMessageBar: false,
	}),
	actions: {
		async getEnabledPromotions({ scrollEnabledList } : { scrollEnabledList: boolean }) {
			const { isAdminModule } = useAppStore();

			const url = isAdminModule ? `/admin/active-promotions.ofer?idCustomer=${this.customerId}` : '/promotion/active-promotions.ofer';

			try {
				const { result } = await serverFetch(url);

				this.enabledPromotions = [...result];

				this.enabledPromotions.forEach((promotion) => {
					promotion.isDisabled = promotion.takenPositions >= 5;
				});

				if (scrollEnabledList) {
					setTimeout(() => {
						this.scrollActivePromotionsList();
					}, 600);
				}
			} catch (error) {
				console.error(error);
			}
		},
		async getSuggestions() {
			const { isAdminModule } = useAppStore();

			let url = isAdminModule ? `/${this.apiNamespace}/get-promotion-suggestions.ofer?idCustomer=${this.customerId}&locationCategoryDTO.categoryId=${this.selectedCategoryId}`
				: `/${this.apiNamespace}/get-promotion-suggestions.ofer?locationCategoryDTO.categoryId=${this.selectedCategoryId}`;

			if (this.selectedLocationId) {
				url = isAdminModule ? `/${this.apiNamespace}/get-promotion-suggestions.ofer?locationCategoryDTO.categoryId=${this.selectedCategoryId}&locationCategoryDTO.locationIdsString=${this.selectedLocationId}`
					: `/${this.apiNamespace}/get-promotion-suggestions.ofer?locationCategoryDTO.categoryId=${this.selectedCategoryId}&locationCategoryDTO.locationIdsString=${this.selectedLocationId}`;
			}

			try {
				const { suggestions } = await serverFetch(url);

				this.suggestions = this.processSugesstions(suggestions);
			} catch (error) {
				console.error(error);
			}
		},
		processSugesstions(suggestions: SuggestedPromotion[]) {
			this.enabledPromotions.forEach((enabledItem) => {
				enabledItem.categoryId = Number(this.selectedCategoryId);
			});

			suggestions.forEach((suggestionItem) => {
				suggestionItem.isDisabled = suggestionItem.takenPositions >= 5;
			});

			if (!this.suggestionsCheckboxFilter) {
				return suggestions;
			}

			return suggestions.filter((suggestionItem) => suggestionItem.isFromCustomerFilters);
		},
		async getPromotionRecommendations() {
			const { isAdminModule } = useAppStore();

			const url = isAdminModule ? `/${this.apiNamespace}/get-promotion-recommendations.ofer?idCustomer=${this.customerId}` : `/${this.apiNamespace}/get-promotion-recommendations.ofer`;

			try {
				const { result } = await serverFetch(url);

				this.suggestions = result;
			} catch (error) {
				console.error(error);
			}
		},
		async getPromotionIndicators() {
			const { hasGallery, hasMarks, hasDescription } = await serverFetch(
				`/promotion/get-profile-promotion-indicators.ofer?idCustomer=${this.customerId}`,
			);

			this.promotionIndicator.hasGallery = hasGallery;
			this.promotionIndicator.hasMarks = hasMarks;
			this.promotionIndicator.hasDescription = hasDescription;
		},
		addToCart(item: Promotion, isPromotionsLimitReached: boolean) {
			if (item.isDisabled) {
				return;
			}

			const { isAdminModule } = useAppStore();

			if (isPromotionsLimitReached && !isAdminModule) {
				this.togglePromotionLimitModal(true);

				return;
			}

			if (!this.cartPromotions.find((cartItem) => cartItem.listUrl === item.listUrl)) {
				this.cartPromotions.push(item);
			}

			if (!isAdminModule) {
				this.saveCartInLocalStorage();
			}
		},
		removeFromCart(item: Promotion) {
			const { isAdminModule } = useAppStore();

			this.cartPromotions = this.cartPromotions.filter((obj) => obj.listUrl !== item.listUrl);

			if (!isAdminModule) {
				this.saveCartInLocalStorage();
			}
		},
		saveCartInLocalStorage() {
			window.localStorage.setItem('cart', JSON.stringify(this.cartPromotions));
		},
		loadCartFromLocalStorage() {
			const cartFromLocalStorage = window.localStorage.getItem('cart');

			this.cartPromotions = cartFromLocalStorage ? JSON.parse(cartFromLocalStorage) : [];
		},
		togglePromotionLimitModal(value: boolean) {
			this.isLimitModalVisible = value;
		},
		scrollActivePromotionsList() {
			const activePromotionsDiv = document.getElementById('active-promotions-items');

			if (!activePromotionsDiv) {
				return;
			}

			activePromotionsDiv.scrollTop = activePromotionsDiv.scrollHeight;
		},
		togglePromotionFeedbackModal(value: boolean) {
			if (!value) {
				setTimeout(() => {
					this.isFeedbackModalVisible = value;
				}, 600);

				return;
			}

			this.isFeedbackModalVisible = value;
		},
		toggleDisableRenewModal({
			event, value, mode, id,
		} : { event?: MouseEvent, value: boolean, mode: string, id?: number }) {
			if (event) {
				event.stopPropagation();
			}

			this.selectedPromotionId = id || 0;

			if (value) {
				this.isDisableRenewModalVisible = value;
				this.isPromotionEnabled = mode === 'disable';
			} else {
				setTimeout(() => {
					this.isDisableRenewModalVisible = value;
					this.isPromotionEnabled = mode === 'disable';
				}, 600);
			}
		},
		async enablePromotions() {
			const { isAdminModule } = useAppStore();

			const purchases = this.promotionPackages.map((promotionPackage) => ({
				idLocation: promotionPackage.locationId,
				idCategory: promotionPackage.categoryId,
				initMonthDuration: Number(promotionPackage.months),
			}));

			this.resetFlags();

			const url = isAdminModule ? `/${this.apiNamespace}/purchase-promotions.ofer?idCustomer=${this.customerId}` : `/${this.apiNamespace}/purchase-promotions.ofer`;

			const response = await serverFetch(
				url,
				{ promotionPurchaseJSON: JSON.stringify({ purchases }) },
				'POST',
			);

			if (response.result === 'not_enough_points') {
				this.isNotEnoughPoints = true;

				await this.getAccountPoints();

				this.isPromotionModalVisible = true;

				return;
			}

			if (response.result !== 'success' && response.result !== 'unpaid_invoice') {
				this.isServerError = true;
				this.isPromotionModalVisible = true;

				return;
			}

			if (response.result === 'unpaid_invoice') {
				this.isUnpaidInvoice = true;
			}

			this.isServerError = false;

			this.enabledListsLength = this.cartPromotions.length;

			this.clearCart({ scrollEnabledList: true });

			if (!this.promotionIndicator.hasGallery || !this.promotionIndicator.hasMarks || !this.promotionIndicator.hasDescription) {
				setTimeout(() => {
					this.isFillProfileModalVisible = true;
				}, 5000);
			}

			this.showConfirmationMessageBar = true;

			if (this.isSummaryPage) {
				this.$router.push({ name: 'PromotionProfile' });
			}

			const target = document.querySelector('.active-promotions');

			if (!target) {
				return;
			}

			const { top } = target.getBoundingClientRect();

			window.setTimeout(() => {
				window.scrollTo({
					top: top - 150 > 0 ? top - 150 : 50,
					behavior: 'smooth',
				});
			}, 150);
		},
		resetFlags() {
			this.isServerError = false;
			this.isNotEnoughPoints = false;
			this.isUnpaidInvoice = false;
		},
		async getAccountPoints() {
			const { isAdminModule } = useAppStore();

			try {
				const url = isAdminModule ? `/admin/get-account-balance.ofer?customerId=${this.customerId}` : '/myoferteo/konto-json/get-account-balance.ofer';

				const { data } = await serverFetch(url);

				this.accountPoints = data.accountBalance;
			} catch (error) {
				console.error(error);
			}
		},
		async clearCart({ scrollEnabledList }: { scrollEnabledList: boolean }) {
			const { isAdminModule } = useAppStore();

			this.cartPromotions.length = 0;

			await this.getEnabledPromotions({ scrollEnabledList });

			this.clearInputs();

			if (!isAdminModule) {
				this.saveCartInLocalStorage();
			}
		},
		clearInputs() {
			this.clearCategoryInput();
			this.clearLocationInput();
		},
		clearCategoryInput() {
			this.categoryInput = '';
			this.selectedCategoryId = '';
			this.suggestions = [];

			this.clearLocationInput();
			this.getPromotionRecommendations();
		},
		clearLocationInput() {
			this.locationInput = '';
			this.selectedLocationId = '';

			if (this.categoryInput === '') {
				this.suggestions = [];
			}

			if (this.selectedCategoryId) {
				this.getSuggestions();
			} else {
				this.getPromotionRecommendations();
			}
		},
	},
});
